import { inject } from '@angular/core';
import { CanMatchFn, Route, UrlSegment } from '@angular/router';
import { FEATURE_FLAGS_SIGNAL_STORE } from '@data-access/system-controls/feature-flags';
import { FeatureFlag } from '@data-access/system-controls/feature-flags/enums';
import { toObservable } from '@angular/core/rxjs-interop';
import { filter, map, take } from 'rxjs/operators';
import { Observable } from 'rxjs';

/**
 * Route guard that determines whether the v2 User Management
 * route should be activated based on the USER_SITE_SERVICE_V2
 * feature flag.
 *
 * Used in PROFILE_ROUTES with a duplicate path ('people/home')
 * so Angular's router picks the first matching route:
 *   - v2 route uses this guard directly
 *   - v1 route uses negateGuard(canMatchUseSiteServiceV2)
 */
export const canMatchUseSiteServiceV2: CanMatchFn = (
  _: Route,
  __: UrlSegment[],
) => {
  /**
   * Inject the feature flags signal store,
   * which is provided at root level.
   */
  const store = inject(FEATURE_FLAGS_SIGNAL_STORE);

  return toObservable(store.$isReady).pipe(
    /**
     * Wait until the store has finished loading feature flags.
     * $isReady starts as false and transitions to true after
     * the initial API call completes (or fails after retries).
     * filter suppresses the initial false emission so downstream
     * operators only run once flags are available.
     */
    filter((ready) => ready),

    /**
     * Read the current value of the USER_SITE_SERVICE_V2 flag.
     * $selectFeatureFlag returns a Signal<boolean>, and the
     * trailing () invokes it to get the boolean value.
     */
    map(() => store.$selectFeatureFlag(FeatureFlag.USER_SITE_SERVICE_V2)()),

    /**
     * Complete the observable after the first emission.
     * The router only needs a single boolean to decide
     * whether this route matches.
     */
    take(1),
  );
};

/**
 * Generic utility that wraps any CanMatchFn and inverts
 * its result. Preserves the return type of the original guard:
 *   - Observable<boolean> → Observable<boolean> (piped with map)
 *   - Promise<boolean>    → Promise<boolean>    (chained with .then)
 *   - boolean             → boolean             (direct negation)
 */
export const negateGuard = (guardFn: CanMatchFn): CanMatchFn => {
  return (route: Route, segments: UrlSegment[]) => {
    /** Execute the original guard to get its result. */
    const result = guardFn(route, segments);

    /**
     * If the guard returns an Observable, pipe it
     * and negate the emitted boolean value.
     */
    if (result instanceof Observable) {
      return result.pipe(
        map((val) => val !== undefined && val !== null && !val),
      );
    }

    /**
     * If the guard returns a Promise, chain a .then
     * to negate the resolved boolean value.
     */
    if (result instanceof Promise) {
      return result.then((val) => val !== undefined && val !== null && !val);
    }

    /**
     * If the guard returns a synchronous boolean,
     * negate and return it directly.
     */
    return result !== undefined && result !== null && !result;
  };
};
